import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";
import { authApi } from "@/lib/api";
import { isValidPassword, PASSWORD_POLICY_MESSAGE } from "@/lib/passwordPolicy";
import { usePasswordPolicyHint } from "@/hooks/usePasswordPolicyHint";

export default function ResetPasswordPage() {
  const passwordHint = usePasswordPolicyHint();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <AuthLayout eyebrow="Reset" headline="Invalid reset link">
        <p data-testid="reset-token-missing" className="text-sm text-slate-600">
          This reset link is missing a token. Request a new one from the forgot-password page.
        </p>
        <Link to="/forgot-password" className="mt-4 inline-flex min-h-10 items-center gap-1 text-sm font-semibold text-indigo-700">
          <ArrowLeft size={14} /> Back to forgot password
        </Link>
      </AuthLayout>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error("Passwords don't match");
    if (!isValidPassword(pw)) {
      return toast.error(passwordHint || PASSWORD_POLICY_MESSAGE);
    }
    setLoading(true);
    try {
      await authApi.reset(token, pw);
      setDone(true);
      toast.success("Password reset — please log in");
      setTimeout(() => navigate("/login"), 800);
    } catch (err) {
      if (err?.code === "invalid_token") {
        toast.error("This reset link is invalid or has expired. Request a new one.", {
          description: "Use Forgot password on the login page to get a fresh link.",
        });
      } else if (err?.code === "weak_password") {
        toast.error(err?.message || PASSWORD_POLICY_MESSAGE);
      } else if (err?.code === "same_password") {
        toast.error(err?.message || "Choose a password you haven't used before.");
      } else {
        toast.error(err?.message || "Reset failed — try again or request a new link");
      }
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <AuthLayout eyebrow="Reset" headline="Password updated">
        <div className="flex items-center gap-2 text-emerald-700 text-sm" data-testid="reset-success">
          <CheckCircle2 size={16} className="shrink-0" /> Redirecting to login…
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout eyebrow="Reset" headline="Choose a new password">
      <form onSubmit={submit} className="space-y-4 min-w-0 w-full" data-testid="reset-password-form">
        <div className="space-y-1.5 min-w-0">
          <Label htmlFor="pw" className="text-[12px]">New password</Label>
          <Input
            id="pw"
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            autoComplete="new-password"
            placeholder="Enter your new password"
            data-testid="reset-new-password"
            required
            minLength={10}
            className="h-11 sm:h-10 w-full min-w-0"
          />
          {passwordHint ? (
            <p className="text-[11px] text-slate-500 break-words">{passwordHint}</p>
          ) : null}
        </div>
        <div className="space-y-1.5 min-w-0">
          <Label htmlFor="pw2" className="text-[12px]">Confirm new password</Label>
          <Input
            id="pw2"
            type="password"
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
            autoComplete="new-password"
            placeholder="Confirm your new password"
            data-testid="reset-confirm-password"
            required
            minLength={10}
            className="h-11 sm:h-10 w-full min-w-0"
          />
        </div>
        <Button type="submit" disabled={loading} className="w-full h-11 sm:h-10" data-testid="reset-submit">
          {loading ? <><Loader2 size={14} className="mr-1 animate-spin" /> Resetting…</> : "Reset password"}
        </Button>
        <Link to="/login" className="mt-2 inline-flex min-h-10 items-center gap-1 text-xs text-slate-600">
          <ArrowLeft size={12} /> Back to login
        </Link>
      </form>
    </AuthLayout>
  );
}
